import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BadgeCheck, Clock, ShieldCheck, Percent, Zap, Sparkles, ArrowRight } from 'lucide-react';
import { useFetch } from '../../hooks/useFetch';
import { servicesApi } from '../../api/servicesApi';
import { settingsApi } from '../../api/contentApi';
import { Button } from '../../components/ui/Button';
import { ServiceOfferCard } from '../../components/ui/ServiceOfferCard';
import { CategoryFilter } from '../../components/ui/CategoryFilter';
import { IconBadge } from '../../components/ui/IconBadge';
import { Reveal } from '../../components/ui/Reveal';
import { Hero } from '../../components/ui/Hero';
import { TrustBar } from '../../components/ui/TrustBar';
import { SkeletonCardGrid } from '../../components/ui/Skeleton';
import { Seo } from '../../components/ui/Seo';
import './SapHome.css';

const AVANTAGES = [
  { icon: BadgeCheck, titre: 'Intervenants qualifiés', description: 'Sélectionnés, formés et suivis pour chaque mission.' },
  { icon: Clock, titre: 'Flexibilité', description: "Ponctuel ou récurrent, à l'heure et au jour qui vous conviennent." },
  { icon: ShieldCheck, titre: 'Sérénité', description: 'Contrats clairs, assurance et remplacement garanti en cas d’absence.' },
];

const AVANTAGES_FISCAUX = [
  {
    icon: Percent,
    titre: "50% de crédit d'impôt",
    description: "Sur l'ensemble de vos dépenses de services à la personne, dans la limite du plafond légal.",
  },
  {
    icon: Zap,
    titre: 'Avance immédiate',
    description: "Ne payez que la moitié du prix : l'avance immédiate déduit directement les 50% dès le paiement, sans avancer les frais.",
  },
];

export function SapHome() {
  const { data: categories } = useFetch(() => servicesApi.categories('particulier'), []);
  const { data: services, loading } = useFetch(
    () => servicesApi.list({ univers: 'particulier', statut: 'publie' }),
    []
  );
  const { data: settings } = useFetch(() => settingsApi.public(), []);
  const [categorieId, setCategorieId] = useState('');

  const heroImage = settings?.['particulier.hero_image_url'];
  const heroEyebrow = settings?.['particulier.hero_eyebrow'] || 'Services à la personne';
  const heroTitre = settings?.['particulier.hero_titre'] || 'Le quotidien, plus léger';
  const heroSousTitre =
    settings?.['particulier.hero_sous_titre'] ||
    "Ménage, repassage, jardinage, petit bricolage, aide à domicile... des professionnels de confiance interviennent chez vous, avec la régularité et le soin d'un vrai partenaire.";

  const categoriesParNom = useMemo(() => {
    const map = {};
    categories?.forEach((cat) => {
      map[cat.id] = cat.nom;
    });
    return map;
  }, [categories]);

  const servicesFiltres = categorieId
    ? services?.filter((s) => String(s.category_id) === categorieId)
    : services;

  return (
    <>
      <Seo
        title="Services à la personne à Cergy — Ménage, jardinage, bricolage"
        description="Ménage, repassage, jardinage et petit bricolage à domicile pour les particuliers, à Cergy, Pontoise, Éragny et dans toute la Communauté d'Agglomération de Cergy-Pontoise. 50% de crédit d'impôt."
        path="/particuliers"
      />

      <Hero
        eyebrow={heroEyebrow}
        title={heroTitre}
        subtitle={heroSousTitre}
        imageUrl={heroImage}
        tone="primary"
      >
        <Link to="/devis">
          <Button className="btn--pulse">Demander un devis gratuit</Button>
        </Link>

        <a href="#pourquoi-nous-choisir" className="hero__fiscal-badge">
          <span className="hero__fiscal-badge-main">
            <span className="hero__fiscal-badge-icon">
              <Percent size={14} strokeWidth={2.6} />
            </span>
            <span className="hero__fiscal-badge-text">
              <strong>50% de crédit d'impôt</strong> + avance immédiate
            </span>
          </span>
          <span className="hero__fiscal-badge-more">
            En savoir plus <ArrowRight size={13} />
          </span>
        </a>
      </Hero>

      <TrustBar />

      <section className="section">
        <div className="container">
          <Reveal as="div" className="section__header offers-header">
            <h2>Nos prestations</h2>
            <p className="section__intro">Choisissez le service qui correspond à votre besoin.</p>

            <CategoryFilter categories={categories} value={categorieId} onChange={setCategorieId} />
          </Reveal>

          {loading && <SkeletonCardGrid />}

          {!loading && servicesFiltres?.length > 0 && (
            <div className="section__grid section__grid--3">
              {servicesFiltres.map((service, i) => (
                <Reveal key={service.id} delay={i * 60}>
                  <ServiceOfferCard
                    service={service}
                    categorieNom={categoriesParNom[service.category_id]}
                    tone="primary"
                  />
                </Reveal>
              ))}
            </div>
          )}

          {!loading && !servicesFiltres?.length && (
            <p>Les services seront bientôt disponibles ici.</p>
          )}
        </div>
      </section>

      <section className="section section--accent" id="pourquoi-nous-choisir">
        <div className="container">
          <Reveal as="div" className="section__header">
            <h2>Pourquoi nous choisir</h2>
          </Reveal>
          <div className="avantages avantages--3">
            {AVANTAGES.map((a, i) => (
              <Reveal as="div" className="avantage" key={a.titre} delay={i * 70}>
                <IconBadge icon={a.icon} tone="primary" />
                <h3>{a.titre}</h3>
                <p>{a.description}</p>
              </Reveal>
            ))}
          </div>

          <Reveal as="div" className="fiscal-highlight" delay={AVANTAGES.length * 70}>
            <span className="fiscal-highlight__badge">
              <Sparkles size={14} /> Avantage fiscal
            </span>
            <div className="fiscal-highlight__grid">
              {AVANTAGES_FISCAUX.map((a) => (
                <div className="fiscal-card" key={a.titre}>
                  <IconBadge icon={a.icon} tone="light" />
                  <h3>{a.titre}</h3>
                  <p>{a.description}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section section--cta">
        <div className="container">
          <h2>Une question avant de vous lancer ?</h2>
          <p className="section__intro">Consultez notre FAQ ou demandez directement un devis gratuit.</p>
          <div className="cta-actions">
            <Link to="/devis">
              <Button>Demander un devis gratuit</Button>
            </Link>
            <Link to="/blog">
              <Button variant="secondary">Voir nos réalisations</Button>
            </Link>
            <Link to="/particuliers/faq">
              <Button variant="secondary">Consulter la FAQ</Button>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
